<script setup lang="ts">
import { profileLocal } from '../profiles/local';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import type { Id } from '@vp/shared-types';
import { boxBounds } from '@vp/core';
import { live } from '../editor/bridge';
import { filterCommands, paletteCommands, type PaletteCommand } from '../editor/commands';
import { objectText, snippet } from '../editor/search';
import { t, typeLabel } from '../i18n';
import { moduleTypeLabel } from '../modules';
import { useApp } from '../stores/app';
import Icon from './Icon.vue';

/**
 * Command palette (Ctrl+K): type a few letters of any command — tools, editing, view, module
 * commands, templates, panels — or of text on the board to jump to that object.
 */
const emit = defineEmits<{ close: []; export: [format: 'png' | 'svg' | 'pdf']; notes: [] }>();
const app = useApp();
const query = ref('');
const index = ref(0);
const input = ref<HTMLInputElement | null>(null);
const list = ref<HTMLElement | null>(null);

const RECENT_KEY = 'vp-palette-recent';
function readRecent(): string[] {
  try {
    const v = JSON.parse(profileLocal.getItem(RECENT_KEY) ?? '[]');
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string').slice(0, 5) : [];
  } catch {
    return [];
  }
}
function remember(id: string): void {
  try {
    profileLocal.setItem(RECENT_KEY, JSON.stringify([id, ...readRecent().filter((x) => x !== id)].slice(0, 5)));
  } catch {
    // a per-viewer convenience: without storage the palette simply has no recent list
  }
}

const all = paletteCommands({ app, exportCanvas: (f) => emit('export', f), openNotes: () => emit('notes') });

interface Row {
  key: string;
  group: string;
  label: string;
  hint?: string;
  keys?: string;
  icon?: string;
  run: () => void;
}

/** Objects whose text matches (at least two letters typed). */
const objectRows = computed<Row[]>(() => {
  const q = query.value.trim().replace(/\s+/g, ' ').toLowerCase();
  const ed = live.editor;
  if (q.length < 2 || !ed) return [];
  const rows: Row[] = [];
  for (const o of ed.doc.allObjects()) {
    if (o.type === 'core:group') continue;
    const text = objectText(ed, o).text;
    if (!text.includes(q)) continue;
    rows.push({ key: 'obj:' + o.id, group: 'objects', label: snippet(text, q, 28), hint: o.name || typeLabel(o.type, moduleTypeLabel), icon: o.type.startsWith('core:') ? o.type.slice(5) : 'module', run: () => jump(o.id) });
    if (rows.length >= 6) break;
  }
  return rows;
});

function jump(id: Id): void {
  const ed = live.editor;
  const o = ed?.doc.get(id);
  if (!ed || !o) return;
  ed.select([id]);
  const cam = live.renderer?.camera;
  if (cam) ed.reveal(() => cam.fit(boxBounds(o), 160, Math.max(1, cam.zoom)));
}

const toRow = (c: PaletteCommand, group: string = c.group): Row => ({ key: group + ':' + c.id, group, label: c.label, keys: c.keys, icon: c.icon, run: () => (remember(c.id), c.run()) });

const rows = computed<Row[]>(() => {
  const q = query.value.trim();
  if (!q) {
    const recent = readRecent()
      .map((id) => all.find((c) => c.id === id))
      .filter((c): c is PaletteCommand => !!c)
      .map((c) => toRow(c, 'recent'));
    return [...recent, ...all.map((c) => toRow(c))];
  }
  return [...filterCommands(all, q).map((c) => toRow(c)), ...objectRows.value];
});

/** Rows with a heading before the first of each group. */
const view = computed(() => rows.value.map((r, i) => ({ ...r, i, head: i === 0 || rows.value[i - 1].group !== r.group ? t('palette.group.' + r.group) : '' })));

watch(query, () => (index.value = 0));
watch(index, async () => {
  await nextTick();
  list.value?.querySelector('.row.on')?.scrollIntoView({ block: 'nearest' });
});

function run(i: number): void {
  const r = rows.value[i];
  if (!r) return;
  emit('close');
  // after the palette is gone: dialogs a command opens get the focus
  void nextTick(() => r.run());
}

function onKey(e: KeyboardEvent): void {
  e.stopPropagation();
  const n = rows.value.length;
  if (e.key === 'Escape') emit('close');
  else if (e.key === 'ArrowDown') index.value = n ? (index.value + 1) % n : 0;
  else if (e.key === 'ArrowUp') index.value = n ? (index.value - 1 + n) % n : 0;
  else if (e.key === 'Enter') run(index.value);
  else return;
  e.preventDefault();
}

onMounted(() => input.value?.focus());
</script>

<template>
  <div class="palette-backdrop" @pointerdown.self="emit('close')">
    <div class="float card palette glass" role="dialog" aria-modal="true" :aria-label="t('palette.title')" @keydown="onKey">
      <div class="field">
        <Icon name="command" :size="16" />
        <input ref="input" v-model="query" :placeholder="t('palette.placeholder')" :aria-label="t('palette.placeholder')" role="combobox" aria-expanded="true" aria-controls="palette-list" :aria-activedescendant="rows.length ? 'pal-' + index : undefined" />
        <kbd>Esc</kbd>
      </div>
      <div id="palette-list" ref="list" class="list" role="listbox">
        <template v-for="r in view" :key="r.key">
          <div v-if="r.head" class="head">{{ r.head }}</div>
          <div :id="'pal-' + r.i" class="row" :class="{ on: r.i === index }" role="option" :aria-selected="r.i === index" :data-command="r.key" @pointermove="index = r.i" @click="run(r.i)">
            <Icon :name="r.icon ?? 'dots'" :size="15" class="ico" />
            <span class="label">{{ r.label }}</span>
            <span v-if="r.hint" class="hint">{{ r.hint }}</span>
            <kbd v-if="r.keys">{{ r.keys }}</kbd>
          </div>
        </template>
        <p v-if="!rows.length" class="empty">{{ t('palette.empty') }}</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.palette-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1900;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 12vh 16px 16px;
  background: color-mix(in srgb, var(--backdrop) 45%, transparent);
}
.palette {
  position: relative;
  width: min(560px, 100%);
  max-height: 64vh;
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
  border-radius: var(--radius-panel);
}
.field {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-bottom: 1px solid var(--border);
  color: var(--muted);
}
.field input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  color: var(--text);
  font: inherit;
  font-size: 15px;
}
.list {
  overflow-y: auto;
  padding: 6px;
}
.head {
  padding: 8px 10px 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--muted);
}
.row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 10px;
  border-radius: var(--radius-panel);
  cursor: pointer;
  font-size: 13.5px;
}
.row.on {
  background: var(--accent-soft);
  color: var(--accent-text);
}
.ico {
  flex: none;
  color: var(--muted);
}
.row.on .ico {
  color: inherit;
}
.label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.hint {
  flex: none;
  max-width: 40%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--muted);
  font-size: 12px;
}
kbd {
  flex: none;
  padding: 1px 7px;
  border-radius: 6px;
  border: 1px solid var(--border);
  background: var(--panel);
  color: var(--muted);
  font: 11px/18px ui-monospace, monospace;
}
.empty {
  margin: 14px;
  text-align: center;
  color: var(--muted);
}
</style>
